import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  GetRequestForQuotationsResponse,
  RequestForQuotationDto,
  UpdateRequestForQuotationRequest,
  UploadRequestForQuotationResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  HttpGetMetaData,
  InternalAPI,
} from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

interface IRfqAPI {
  getRfqList: (
    metadata: HttpGetMetaData<RequestForQuotationDto>,
  ) => Promise<GetRequestForQuotationsResponse>
  uploadRfq: (file: File) => Promise<UploadRequestForQuotationResponse>
  getRFQById: (rfqId: string) => Promise<RequestForQuotationDto>
  deleteRfq: (rfqId: string) => Promise<void>
  updateRfq: (
    rfqId: string,
    req: UpdateRequestForQuotationRequest,
  ) => Promise<void>
  createProject: (rfqId: string, buyerRelationId?: string) => Promise<void>
  createRFQFromProject(
    forwardId: string,
    forwardSender: string,
    projectId: string,
  ): Promise<string>
}

class RfqAPI extends InternalAPI implements IRfqAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}/rfq`, onRequestChange)
  }
  public async createRFQFromProject(
    forwardId: string,
    forwardSender: string,
    projectId: string,
  ): Promise<string> {
    return this.PostAsync({
      id: `GenerateRFQInSCSN-${projectId}`,
      relativePath: '/createRFQFromProject',
      data: { forwardId, forwardSender, buyerProjectId: projectId },
    })
  }

  public async getRfqList(metadata: HttpGetMetaData<RequestForQuotationDto>) {
    return this.GetPagedAsync<GetRequestForQuotationsResponse>({
      relativePath: '',
      _meta: metadata,
      id: 'GetRfqList',
    })
  }

  public async uploadRfq(file: File) {
    const formData = new FormData()
    formData.append('file', file)

    return this.PostAsync<UploadRequestForQuotationResponse>({
      relativePath: '',
      data: formData,
      id: 'UploadRfq',
    })
  }

  public async getRFQById(rfqId: string): Promise<RequestForQuotationDto> {
    return this.GetAsync<RequestForQuotationDto>({
      relativePath: `/${rfqId}`,
      id: `GetRfqById-${rfqId}`,
    })
  }

  public async deleteRfq(rfqId: string): Promise<void> {
    return this.DeleteAsync({
      relativePath: `/${rfqId}`,
      id: `DeleteRfq-${rfqId}`,
    })
  }

  public async updateRfq(
    rfqId: string,
    req: UpdateRequestForQuotationRequest,
  ): Promise<void> {
    return this.PutAsync({
      relativePath: `/${rfqId}`,
      data: req,
      id: `UpdateRfq-${rfqId}`,
    })
  }

  public async createProject(
    rfqId: string,
    buyerRelationId?: string,
  ): Promise<void> {
    return this.PostAsync({
      relativePath: `/${rfqId}/CreateProject`,
      id: `CreateProjectFromRfq-${rfqId}`,
      data: buyerRelationId ? { buyerRelationId } : undefined,
    })
  }
}

export class RequestForQuotationController extends BaseController<IRfqAPI> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new RfqAPI(partyId, onRequestChange)
    })
  }

  public async GetRfqList(
    metadata: HttpGetMetaData<RequestForQuotationDto>,
  ): Promise<GetRequestForQuotationsResponse> {
    try {
      return await this.api.getRfqList(metadata)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async UploadRfq(
    file: File,
  ): Promise<UploadRequestForQuotationResponse> {
    try {
      return await this.api.uploadRfq(file)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetRfqById(rfqId: string): Promise<RequestForQuotationDto> {
    try {
      return await this.api.getRFQById(rfqId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async DeleteRfq(rfqId: string): Promise<void> {
    try {
      return await this.api.deleteRfq(rfqId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async UpdateRfq(
    rfqId: string,
    req: UpdateRequestForQuotationRequest,
  ): Promise<void> {
    try {
      return await this.api.updateRfq(rfqId, req)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async CreateProject(
    rfqId: string,
    buyerRelationId?: string,
  ): Promise<void> {
    try {
      return await this.api.createProject(rfqId, buyerRelationId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async CreateRFQFromProject(
    forwardId: string,
    forwardSender: string,
    projectId: string,
  ) {
    try {
      return await this.api.createRFQFromProject(
        forwardId,
        forwardSender,
        projectId,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
